import { Theme } from '@/app'
import { useQuery } from '@tanstack/react-query'
import { CSSProperties } from 'react'

export const getImageSize = (url: string) => {
  return new Promise<{ width: number; height: number }>((resolve, reject) => {
    const img = new Image()

    img.src = url

    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      })
    }

    img.onerror = () => {
      reject(new Error('Fail to get image size'))
    }
  })
}

export const useImageSize = (url: string) => {
  return useQuery({
    queryKey: ['image-size', url],
    queryFn: () => getImageSize(url),
    enabled: !!url,
    staleTime: Infinity,
    retry: 3,
  })
}

type ImageSizeStyleParams = {
  size: ReturnType<typeof useImageSize>
  maxHeight: number | string
  maxWidth: number | string
  maxMobileHeight?: number | string
  maxMobileWidth?: number | string
}

export function getImageSizeStyle(params: ImageSizeStyleParams): CSSProperties {
  const { size, maxHeight, maxWidth, maxMobileHeight = maxHeight, maxMobileWidth = maxWidth } = params

  const isLandscape = size.data.width > size.data.height
  const ratio = size.data.width / size.data.height
  const isNearSquare = Math.abs(ratio - 1) < 0.2

  let style: CSSProperties = {
    height: maxHeight,
    aspectRatio: ratio,
    width: 'auto',

    [Theme.media.down('small')]: {
      height: maxMobileHeight,
    },
  }

  if (isLandscape) {
    style = {
      width: maxWidth,
      aspectRatio: ratio,
      height: 'auto',

      [Theme.media.down('small')]: {
        height: maxMobileWidth,
      },
    }
  }

  if (isNearSquare) {
    style = {
      width: '100%',
      height: '100%',
      aspectRatio: null,
    }
  }

  return style
}
